import { useEffect, useRef, useState } from 'react'

/**
 * Adds an `is-visible` class (via the returned `isVisible` flag) the first time
 * the element scrolls into view. Used together with the `.reveal` CSS utility
 * for a subtle fade-up animation. Reveals only once, then unobserves.
 */
export function useReveal(options = {}) {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Graceful fallback if IntersectionObserver is unavailable.
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [options])

  return { ref, isVisible }
}
